import type { CSSProperties, ReactNode } from 'react';
import { KpiCard, Delta, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { Bilgi } from '../satis-analiz/Bilgi';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import type { Donem } from '../satis-analiz/donem';
import type { StokKaynaklari } from './ekranKaynaklari';
import { useStokSorgu } from './useStokVerisi';
import { kayipCiroOrani, kovalariBirlestir, oranTopla, type KayipCiroSatiri, type KovaSatiri } from './toplama';
import { kapsamNotuGorunsunMu } from './kapsam';
import { fmtCarpan } from './bicimYardimcilari';
import { KartHatasi } from './KartHatasi';

interface Props {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  oncekiDonem: { bas: string; bit: string };
  sube?: string | null;
  kategori?: string | null;
}

interface KayipOzetSatiri { tur: string; tutar: number }
interface EnvanterSatiri { envanter_degeri: number }
interface DevirSatiri { donem_cogs: number; ort_envanter_deger: number }
interface SellThroughSatiri { alinan: number; satilan: number }

const BUGUN_BILGI = 'Stok ölçüleri bugünün fotoğrafıdır, dönem filtresinden etkilenmez.';
const FIRE_BILGI = 'Oran, satış satırlarının KDV hariç cirosuna göredir (fire ile aynı taban).';

/** K2: "Kayıp" başlığı yalnız fire (tür 16); sarf ayrı satırda kalır, buraya karışmaz. */
function fireToplami(satirlar: KayipOzetSatiri[]): number {
  return satirlar.filter((s) => s.tur === 'fire').reduce((t, s) => t + (Number(s.tutar) || 0), 0);
}

function yuzdeDegisim(bu: number, onceki: number): number | null {
  return onceki !== 0 ? ((bu - onceki) / Math.abs(onceki)) * 100 : null;
}

/** Türkçe yüzde biçimi: işaret NUMARADAN ÖNCE ("%2,18"), primitives'in `fmtPct`i sondan basar. */
function fmtOran(n: number, decimals = 2): string {
  return `%${n.toFixed(decimals).replace('.', ',')}`;
}

/** Puan farkı (yüzde noktası) — oranın kendisiyle KARIŞTIRILMAZ, ayrı birim. */
function fmtPuan(n: number, decimals = 2): string {
  const isaret = n > 0 ? '+' : n < 0 ? '−' : '';
  return `${isaret}${Math.abs(n).toFixed(decimals).replace('.', ',')} puan`;
}

/**
 * Delta rozeti ya da onun YERİ (satis-analiz/OzetSerit.tsx:94'teki `Rozet`in kopyası — CLS'i
 * önlemek için aynı 21px yer tutucu). Önceki dönemde kıyaslanacak veri yoksa "—".
 */
function Rozet({ deger, hazir }: { deger: number | null; hazir: boolean }) {
  if (!hazir) return <span className="block h-[21px]" aria-hidden="true" data-testid="rozet-yeri" />;
  if (deger === null)
    return (
      <span className="block h-[21px] text-ink-400" data-testid="delta-yok" title="Önceki dönemde karşılaştırılacak veri yok">
        —
      </span>
    );
  return <Delta value={deger} decimals={1} />;
}

/**
 * satis-analiz/OzetSerit.tsx:51'deki dosya-yerel `Kart` sarmalayıcısının kopyası (export edilen bir
 * ortak bileşen DEĞİL — plan bunu birebir kopyalamayı ister). Kart ölçü kabidir; tutar kart genişliğine
 * `min(28px, (kart - 42px)/(karakter x 0.6))` ile sığar.
 */
function Kart({
  testid, label, value, alt, bilgi,
}: { testid: string; label: string; value: string; alt: ReactNode; bilgi?: ReactNode }) {
  const olcu = { containerType: 'inline-size', '--sa-n': Math.max(value.length, 1) } as CSSProperties;
  return (
    <div
      data-testid={testid}
      style={olcu}
      className="min-w-0 flex flex-col [&>div]:flex-1 [&_.mono]:[font-size:min(28px,calc((100cqi_-_42px)/(var(--sa-n)*0.6)))]"
    >
      {/* fmtTRY kendi ₺ isaretini basar; prefix="" cift sembolu onler. */}
      <KpiCard
        label={<span className="inline-flex items-center gap-1.5">{label}{bilgi}</span>}
        prefix=""
        value={value}
        sub={<div className="flex flex-col gap-1">{alt}</div>}
      />
    </div>
  );
}

const BOS_ALT = <span className="block h-[21px]" aria-hidden="true" />;

/**
 * Spec §4.1 — Özet şerit, 5 kart: Fire · Eldeki stok değeri · 90+ bekleyen · Devir hızı · Sell-through.
 * Fire (990059 + 990065) K1'in omurgası ve TEK Δ karşılaştırmalı karttır (önceki eşit dönem).
 * Kart 2/3 anlık ölçüler (K4): dönem filtresinden etkilenmez, ⓘ "bugünün fotoğrafı" notu taşır.
 */
export function OzetSerit({ kaynaklar, donem, oncekiDonem, sube = null, kategori = null }: Props) {
  const f = { donem, sube, kategori };
  const oncekiDonemTam: Donem = {
    bas: oncekiDonem.bas, bit: oncekiDonem.bit, tekGun: oncekiDonem.bas === oncekiDonem.bit,
  };
  const fOnceki = { donem: oncekiDonemTam, sube, kategori };

  // Fire — headline değer 990059'dan (kayıp özeti), oran 990065'ten (dönem, gün tanesi).
  const ozetQ = useStokSorgu<KayipOzetSatiri>(kaynaklar.kayipOzet, f);
  const ozetOncekiQ = useStokSorgu<KayipOzetSatiri>(kaynaklar.kayipOzet, fOnceki);
  const ciroQ = useStokSorgu<KayipCiroSatiri>(kaynaklar.kayipCiro, f);
  const ciroOncekiQ = useStokSorgu<KayipCiroSatiri>(kaynaklar.kayipCiro, fOnceki);
  // Anlık ölçüler (K4) — dönem parametresi ds'ye gitmez (990023/990043 parametresiz), ama
  // `useStokSorgu` aynı çağrı şekli için `f` geçiliyor; ds tarih bildirmediğinden yok sayılır.
  const envanterQ = useStokSorgu<EnvanterSatiri>(kaynaklar.envanter, f);
  const yaslandirmaQ = useStokSorgu<KovaSatiri>(kaynaklar.yaslandirma, f);
  // Akış ölçüleri (dönem) — Δ YOK (yalnız Fire'ın iki ds'i ikinci pencereyle çağrılır).
  const devirQ = useStokSorgu<DevirSatiri>(kaynaklar.devirHizi, f);
  const sellQ = useStokSorgu<SellThroughSatiri>(kaynaklar.sellThrough, f);

  if (!kaynaklar.kayipOzet && !kaynaklar.envanter && !kaynaklar.yaslandirma
      && !kaynaklar.devirHizi && !kaynaklar.sellThrough) return null;

  // ── Fire ──────────────────────────────────────────────────────────────────────────────────
  const buFire = ozetQ.data ? fireToplami(ozetQ.data.data) : null;
  const oncekiFire = ozetOncekiQ.data ? fireToplami(ozetOncekiQ.data.data) : null;
  const oranBu = ciroQ.data ? kayipCiroOrani(ciroQ.data.data).oran : null;
  const oncekiOran = ciroOncekiQ.data ? kayipCiroOrani(ciroOncekiQ.data.data).oran : null;
  const fireYukleniyor = !!kaynaklar.kayipOzet && (ozetQ.isLoading || ciroQ.isLoading);
  // F-4 — headline (`ozetQ`) ya da oran (`ciroQ`) sorgularından biri hata verirse kart "—" ile
  // sessizce yanıltmaz, dürüst bir hata satırı gösterir.
  const fireHata = !!kaynaklar.kayipOzet && (ozetQ.isError || ciroQ.isError);
  const fireHazir = buFire !== null && oncekiFire !== null;
  const puanFarki = oranBu !== null && oncekiOran !== null ? oranBu - oncekiOran : null;

  // ── Eldeki stok değeri (bugün) ───────────────────────────────────────────────────────────
  const envanterToplam = envanterQ.data
    ? envanterQ.data.data.reduce((t, r) => t + (Number(r.envanter_degeri) || 0), 0)
    : null;
  const envanterYukleniyor = !!kaynaklar.envanter && envanterQ.isLoading;
  const envanterHata = !!kaynaklar.envanter && envanterQ.isError;

  // ── 90+ gün bekleyen (bugün) ─────────────────────────────────────────────────────────────
  const kovalar = yaslandirmaQ.data ? kovalariBirlestir(yaslandirmaQ.data.data) : null;
  const doksanArti = kovalar ? (kovalar.find((k) => k.kova === '90+ gün') ?? null) : null;
  const yaslandirmaYukleniyor = !!kaynaklar.yaslandirma && yaslandirmaQ.isLoading;
  const yaslandirmaHata = !!kaynaklar.yaslandirma && yaslandirmaQ.isError;

  // ── Devir hızı (dönem) ───────────────────────────────────────────────────────────────────
  const devirOran = devirQ.data ? oranTopla(devirQ.data.data, 'donem_cogs', 'ort_envanter_deger') : null;
  const devirYukleniyor = !!kaynaklar.devirHizi && devirQ.isLoading;
  const devirHata = !!kaynaklar.devirHizi && devirQ.isError;

  // ── Sell-through (dönem) ─────────────────────────────────────────────────────────────────
  const sellOranHam = sellQ.data ? oranTopla(sellQ.data.data, 'satilan', 'alinan') : null;
  const sellOran = sellOranHam !== null ? sellOranHam * 100 : null;
  const sellYukleniyor = !!kaynaklar.sellThrough && sellQ.isLoading;
  const sellHata = !!kaynaklar.sellThrough && sellQ.isError;

  // ── I-3 (final-fix F-3/F-5) ──────────────────────────────────────────────────────────────
  // Beş kartın dördü (envanter/yaşlandırma/devirHizi/sellThrough → 990023/990043/990048/990046)
  // `@LOKASYON` bildirmiyor; yalnız Fire (990059) şubeye süzülüyor. `kapsamNotuGorunsunMu` AYNI
  // mekanizmayla (F-5) bu dört ds'ten en az biri bildirmezken şube süzgeci doluysa notu gösterir —
  // `kayipOzet` KASITLI OLARAK dışarıda tutulur (o zaten süzülüyor, notun konusu DEĞİL).
  const kapsamNotuGorunur = kapsamNotuGorunsunMu(
    [kaynaklar.envanter, kaynaklar.yaslandirma, kaynaklar.devirHizi, kaynaklar.sellThrough],
    '@LOKASYON',
    sube,
  );

  return (
    <div data-testid="stok-ozet-serit" className="flex flex-col gap-1.5">
      {kapsamNotuGorunur && (
        <p data-testid="serit-kapsam-not" className="text-[11px] text-ink-400">
          Fire dışındaki kartlar (Eldeki stok değeri, 90+ gün bekleyen, Devir hızı, Sell-through) şube
          süzgecinden etkilenmez — tüm şubeleri kapsar.
        </p>
      )}
      <div data-testid="stok-ozet-serit-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {kaynaklar.kayipOzet && (
          fireYukleniyor ? (
            <BlokIskeleti testid="serit-fire" yukseklik={88} satir={2} />
          ) : fireHata ? (
            <KartHatasi testid="serit-fire-hata" yukseklik={88} mesaj="Fire yüklenemedi." />
          ) : (
            <Kart
              testid="serit-fire"
              label="Fire"
              value={buFire !== null ? fmtTRY(buFire) : '—'}
              bilgi={<Bilgi testid="serit-fire-bilgi" metin={FIRE_BILGI} />}
              alt={
                <>
                  <Rozet hazir={fireHazir} deger={fireHazir ? yuzdeDegisim(buFire as number, oncekiFire as number) : null} />
                  {oranBu !== null && (
                    <span data-testid="serit-fire-oran" className="text-[12px] text-ink-500">
                      Ciroya oran {fmtOran(oranBu)}
                      {puanFarki !== null && <> · {fmtPuan(puanFarki)}</>}
                    </span>
                  )}
                </>
              }
            />
          )
        )}

        {kaynaklar.envanter && (
          envanterYukleniyor ? (
            <BlokIskeleti testid="serit-envanter" yukseklik={88} satir={2} />
          ) : envanterHata ? (
            <KartHatasi testid="serit-envanter-hata" yukseklik={88} mesaj="Eldeki stok değeri yüklenemedi." />
          ) : (
            <Kart
              testid="serit-envanter"
              label="Eldeki stok değeri"
              value={envanterToplam !== null ? fmtTRY(envanterToplam) : '—'}
              bilgi={<Bilgi testid="serit-envanter-bilgi" metin={BUGUN_BILGI} />}
              alt={BOS_ALT}
            />
          )
        )}

        {kaynaklar.yaslandirma && (
          yaslandirmaYukleniyor ? (
            <BlokIskeleti testid="serit-bekleyen" yukseklik={88} satir={2} />
          ) : yaslandirmaHata ? (
            <KartHatasi testid="serit-bekleyen-hata" yukseklik={88} mesaj="90+ gün bekleyen yüklenemedi." />
          ) : (
            <Kart
              testid="serit-bekleyen"
              label="90+ gün bekleyen"
              value={doksanArti ? fmtTRY(doksanArti.kalan_deger) : '—'}
              bilgi={<Bilgi testid="serit-bekleyen-bilgi" metin={BUGUN_BILGI} />}
              alt={
                doksanArti ? (
                  <span className="text-[12px] text-ink-500">{fmtNum(doksanArti.parti_sayisi)} parti</span>
                ) : BOS_ALT
              }
            />
          )
        )}

        {kaynaklar.devirHizi && (
          devirYukleniyor ? (
            <BlokIskeleti testid="serit-devir" yukseklik={88} satir={2} />
          ) : devirHata ? (
            <KartHatasi testid="serit-devir-hata" yukseklik={88} mesaj="Devir hızı yüklenemedi." />
          ) : (
            <Kart
              testid="serit-devir"
              label="Devir hızı"
              value={devirOran !== null ? fmtCarpan(devirOran) : '—'}
              alt={BOS_ALT}
            />
          )
        )}

        {kaynaklar.sellThrough && (
          sellYukleniyor ? (
            <BlokIskeleti testid="serit-sellthrough" yukseklik={88} satir={2} />
          ) : sellHata ? (
            <KartHatasi testid="serit-sellthrough-hata" yukseklik={88} mesaj="Sell-through yüklenemedi." />
          ) : (
            <Kart
              testid="serit-sellthrough"
              label="Sell-through"
              value={sellOran !== null ? fmtOran(sellOran) : '—'}
              alt={BOS_ALT}
            />
          )
        )}
      </div>
    </div>
  );
}
